/*******************************************************************************
  설  명 : 홈페이지 > 비밀번호 찾기
  작성자 : 송영석
  작성일 : 2020-06-08
*******************************************************************************/
import { Component, OnInit, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { config } from '@app/service/config.service';
import { ApiResponse } from '@app/service/api-response';
import { UtilService } from '@app/service/util.service';
import { CommonService } from '@app/service/common.service';

import { HAuthService } from '@home/service/auth.service';
import { HMemberService } from '@home/service/member.service';

import { O2OZCMS } from '@app/components/o2oz-cms/o2oz-cms.component';

declare global {
  interface Window {
    fn: any;
  }
}

@Component({
  selector: 'app-find-pw',
  templateUrl: './find-pw.component.html',
  styleUrls: ['./find-pw.component.scss']
})
export class FindPwComponent extends O2OZCMS implements OnInit {

  /*******************************************************************************
    설명 : 전역 변수 선언부
  *******************************************************************************/
  errorResponse: ApiResponse;
  public form: FormGroup;
  public formErrors = {};

  public isLoginCheck = false;

  public isSelectOn: boolean; //selectbox on
  public isSelectTxt = '직접입력'; // selectbox txt

  /*******************************************************************************
    설  명 : 생성자
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  constructor(
    router: Router,
    commonService: CommonService,
    activatedRoute: ActivatedRoute,
    toastrService: ToastrService,
    private utilService: UtilService,
    private formBuilder: FormBuilder,
    private ngZone: NgZone,
    private authService: HAuthService,
    private memberService: HMemberService
  ) {
    // 상위 클래스에 router 전달
    super(router, commonService, activatedRoute, toastrService);

    this.authService.getIsHomeLogin.subscribe(data => {
      this.isLoginCheck = data;
    });

    if ( this.isLoginCheck === true ) {
      this.router.navigate(['/']);
    }

    this.buildForm();
  }

  /*******************************************************************************
    설  명 : 빌드폼 생성
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  buildForm(): void {
    this.form = this.formBuilder.group({
      userId: [ '', [Validators.required, Validators.pattern('^[a-z0-9]{5,20}$')] ],
      userName: [ '', [Validators.required] ],
      email1: [ '', [Validators.required, Validators.pattern('^[a-z0-9._-]*$')] ],
      email2: [ '', [] ],
    });

    this.form.valueChanges.subscribe(data => {
      this.utilService.updateFormErrors( this.form, this.formErrors );
    });
  }

  ngOnInit(): void {
    window.fn  = window.fn || {};
    window.fn.namespace = window.fn.namespace || {};
    window.fn.namespace.hpAuthFunc = this.hpAuthFunc.bind(this);
    window.fn.namespace.ipinAuthFunc = this.ipinAuthFunc.bind(this);
  }

  ngDoCheck() {
    // 이메일 선택에 따른 패턴 부여
    if ( this.form.get('email2').value == '' ) {
      this.form.get('email1').setValidators( [Validators.required, Validators.pattern('^[a-z0-9._-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')] );
      this.form.get('email1').updateValueAndValidity();
    } else {
      this.form.get('email1').setValidators( [Validators.required, Validators.pattern('^[a-z0-9._-]*$')] );
      this.form.get('email1').updateValueAndValidity();
    }
  }

  /*******************************************************************************
    설  명 : SELECT 선택 처리
  *******************************************************************************/
  selectChange( event: any ) {
    // console.log(event);
    this.isSelectTxt = event.target.innerText;

    if ( this.isSelectTxt == '직접입력' ) {
      this.form.get( 'email2' ).setValue( '' );
    } else {
      this.form.get( 'email2' ).setValue( this.isSelectTxt );
    }
  }

  /*******************************************************************************
    설  명 : 비밀번호찾기 처리
  *******************************************************************************/
  public setFindPw() {
    this.utilService.makeFormDirtyAndUpdateErrors(this.form, this.formErrors);

    if ( this.form.valid ) {
      this.memberService.setFindPw( this.form ).then(response => {
        if ( response.ResultCode == true ) {
          this.toastrService.success(response.ResultMessage, '비밀번호찾기');

          sessionStorage.setItem( 'findResult', JSON.stringify( response.data ) );

          this.router.navigate(['/member/findpw/result']);
        } else {
          this.toastrService.error(response.ResultMessage, '비밀번호찾기');
        }
      })
      .catch(response => {
        this.errorResponse = response;
        this.utilService.handleFormSubmitError(this.errorResponse, this.form, this.formErrors);
      });
    } else {
      this.toastrService.error( '필수 입력값을 다시 한번 확인하시기 바랍니다.', '비밀번호찾기');
    }
  }

  /*******************************************************************************
    설  명 : 회원 정보 가져오기
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  setFindDikey( dikey: any ) {
    this.memberService.setFindDikey( dikey ).then( response => {
      //console.log(response);
      if( response.ResultCode ) {
        sessionStorage.setItem( 'findResult', JSON.stringify( response.data ) );
        this.router.navigate(['/member/findpw/result']);
      } else {
        this.toastrService.error( response.ResultMessage );
      }
    });
  }

  /*******************************************************************************
    설  명 : 인증 실행 후 ngZone 실행
  *******************************************************************************/
  hpAuthFunc( response: any ) {
    this.ngZone.run(() => this.hpAuthCallback( response ));
  }

  ipinAuthFunc( response: any ) {
    this.ngZone.run(() => this.ipinAuthCallback( response ));
  }

  /*******************************************************************************
    설  명 : 본인인증 체크
  *******************************************************************************/
  authCheck( type: any ) {
    if ( type === 'H' ) {
      window.open( config.baseUrl + '/checkplus/checkplus_main.php', '', 'width=500,height=550,scrollbar=yes' );
      // this.hpAuthCallback('');
    } else if ( type === 'I' ) {
      window.open( config.baseUrl + '/ipin/ipin_request.php', '', 'width=500,height=550,scrollbar=yes' );
      // this.ipinAuthCallback('');
    } else {
      this.toastrService.error('정상적으로 실행하시기 바랍니다.', '');
    }
  }

  /*******************************************************************************
    설  명 : 휴대폰 인증 콜백
  *******************************************************************************/
  hpAuthCallback( response: any ) {
    // console.log(response);
    this.setFindDikey( response.dupinfo );
  }

  /*******************************************************************************
    설  명 : 아이핀 인증 콜백
  *******************************************************************************/
  ipinAuthCallback( response: any ) {
    // console.log(response);
    this.setFindDikey( response.dupinfo );
  }
}
